import { useState } from 'react';
import { ConfirmDialog, NamePromptDialog } from '@/theater/dialogs';
import type { TheaterSession, TheaterState } from '@/theater/TheaterSession';
import { hexToRgba, rgbaToCss, rgbaToHex } from './colors';
import { SECTIONS, type Shape } from './model';
import { Icon } from '@/ui/Icon';

const SHAPES: Shape[] = ['CURVE', 'MASK', 'POINT', 'LINE', 'ANGLE', 'PLANE'];
const MARGIN_ROWS: Record<string, string> = { RnLnFreeMargin: 'RN_LN_FreeMargin', RnRlFreeMargin: 'RN_RL_FreeMargin', RlLnFreeMargin: 'RL_LN_FreeMargin' };

export function InventoryPanel({ session, state }: { session: TheaterSession; state: TheaterState }) {
  const [filter, setFilter] = useState<Set<Shape>>(new Set());
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggleFilter = (s: Shape) => {
    const next = new Set(filter);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setFilter(next);
  };
  const anyVisible = [...state.rows.values()].some((r) => r.visible);
  return (
    <div className="panel inventory">
      <div className="row inv-toolbar">
        <h3>Inventory</h3>
        <span className="spacer" />
        <button className="link small" title="Show / hide all" onClick={() => session.showHideAll()}>
          {anyVisible ? 'Hide all' : 'Show all'}
        </button>
        <button className="link small" title="Show default inventory" onClick={() => session.showDefaultInventory()}>
          Defaults
        </button>
      </div>
      {state.corrections && <SavesBlock session={session} state={state} />}
      {state.surfaceErrors.length > 0 && (
        <div className="error small" title={state.surfaceErrors.join(', ')}>
          {state.surfaceErrors.length} segmentation surface{state.surfaceErrors.length === 1 ? '' : 's'} failed to load ({state.surfaceErrors.slice(0, 3).join(', ')}
          {state.surfaceErrors.length > 3 ? ', …' : ''}). Contours cannot be re-sliced and points cannot snap to surfaces.
        </div>
      )}
      <div className="row wrap inv-filter">
        {SHAPES.map((s) => (
          <button key={s} className={`chip ${filter.has(s) ? 'on' : ''}`} onClick={() => toggleFilter(s)}>
            {s.toLowerCase()}
          </button>
        ))}
      </div>
      {SECTIONS.map((section) => {
        const rows = section.rows.filter((r) => state.rows.get(r.key)?.present && (filter.size === 0 || filter.has(r.shape)));
        if (!rows.length) return null;
        const vis = rows.filter((r) => state.rows.get(r.key)!.visible);
        const isOpen = open[section.title] ?? true;
        return (
          <div key={section.title} className="inv-section">
            <div className="inv-section-head">
              <button className="link" onClick={() => setOpen({ ...open, [section.title]: !isOpen })}>
                <Icon name="chevron-right" className={isOpen ? 'chev open' : 'chev'} /> {section.title}
              </button>
              <span className="spacer" />
              <button className={`icon ${vis.length > 0 ? 'on' : ''}`} title="Show / hide this section" onClick={() => session.setSectionVisible(section.title, !(vis.length > 0))}>
                <Icon name={vis.length > 0 ? 'eye' : 'eye-off'} />
              </button>
              <button className="icon" title="Show only this section" onClick={() => session.isolateSection(section.title)}>
                <Icon name="star" />
              </button>
            </div>
            {isOpen &&
              rows.map((r) => {
                const row = state.rows.get(r.key)!;
                return <Row key={r.key} title={r.title} row={row} session={session} state={state} />;
              })}
          </div>
        );
      })}
    </div>
  );
}

/** The Saves panel (MeasurementPanelView.Corrections): "Original (AI)" plus the saved versions,
 * Save (overwrite the active version), Save As (new slug from the label), Delete. */
function SavesBlock({ session, state }: { session: TheaterSession; state: TheaterState }) {
  const c = state.corrections!;
  const [prompt, setPrompt] = useState(false);
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="saves">
      <div className="row">
        <h3>Saves</h3>
        {c.dirty && (
          <span className="badge dirty" title="Edits not saved to a version">
            unsaved
          </span>
        )}
        {c.skipped > 0 && (
          <span className="badge muted" title="Entries of this version that no longer match the prediction (the AI point moved more than 1 mm, or is missing); they are kept on save">
            {c.skipped} skipped
          </span>
        )}
        <span className="spacer" />
        {c.busy && <span className="muted small">…</span>}
      </div>
      <div className="row">
        <select value={c.activeSlug ?? ''} disabled={c.busy} title="Saved correction versions (switching drops unsaved edits)" onChange={(e) => void session.activateCorrections(e.target.value || null)}>
          <option value="">Original (AI)</option>
          {c.versions.map((v) => (
            <option key={v.slug} value={v.slug}>
              {v.label}
            </option>
          ))}
        </select>
        <button className="secondary small" disabled={c.busy || !c.dirty || !c.activeSlug} title="Save the edits into the active version" onClick={() => void session.saveCorrections()}>
          Save
        </button>
        <button className="secondary small" disabled={c.busy || !c.dirty} title="Save the edits as a new version" onClick={() => setPrompt(true)}>
          Save as…
        </button>
        <button className="secondary small" disabled={c.busy || !c.activeSlug} title="Delete the active version" onClick={() => setConfirm(true)}>
          Delete
        </button>
      </div>
      {c.error && <div className="error small">{c.error}</div>}
      {c.notice && <div className="notice small">{c.notice}</div>}
      {prompt && (
        <NamePromptDialog
          title="Save corrections as"
          label="Version name"
          initial={c.activeLabel ?? ''}
          onSubmit={async (name) => {
            const ok = await session.saveCorrectionsAs(name);
            if (ok) setPrompt(false);
            return ok;
          }}
          onClose={() => setPrompt(false)}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title="Delete version"
          message={`Delete "${c.activeLabel ?? c.activeSlug}"? The AI prediction stays; this cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={async () => {
            await session.deleteCorrections();
            setConfirm(false);
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}

function Row({ title, row, session, state }: { title: string; row: TheaterState['rows'] extends Map<string, infer R> ? R : never; session: TheaterSession; state: TheaterState }) {
  const [info, setInfo] = useState(false);
  const alpha = row.color[3];
  const canOpacity = !/Area$/.test(row.key);
  const corr = state.corrections;
  const editable = !!corr && !!session.corrections?.isEditableRow(row.key);
  const editing = editable && corr!.editing.has(row.key);
  const edited = !!corr && corr.edited.has(row.key);
  const marginStructure = MARGIN_ROWS[row.key];
  const marginEdited = !!marginStructure && !!session.corrections?.isMarginEdited(marginStructure);
  return (
    <div className={`inv-row ${row.visible ? '' : 'off'}`}>
      <div className="inv-row-main">
        <input
          type="color"
          className="swatch"
          value={rgbaToHex(row.color)}
          title="Colour"
          onChange={(e) => session.setRowColor(row.key, hexToRgba(e.target.value, row.color[3]))}
          style={{ background: rgbaToCss(row.color) }}
        />
        <button className="link title" onClick={() => setInfo(!info)} title={row.info.length ? 'Details' : undefined}>
          {title}
        </button>
        <span className="value mono">{row.value}</span>
        {canOpacity && (
          <span className="opacity" title={`Opacity ${(alpha * 100).toFixed(0)}%`}>
            <button className="icon tiny" onClick={() => session.setRowColor(row.key, [row.color[0], row.color[1], row.color[2], Math.max(0, alpha - 0.1)])}>
              −
            </button>
            <span className="bar" style={{ background: rgbaToCss([row.color[0], row.color[1], row.color[2], alpha * 0.2]) }}>
              <span style={{ width: `${alpha * 100}%`, background: rgbaToCss([row.color[0], row.color[1], row.color[2], 1]) }} />
            </span>
            <button className="icon tiny" onClick={() => session.setRowColor(row.key, [row.color[0], row.color[1], row.color[2], Math.min(1, alpha + 0.1)])}>
              +
            </button>
          </span>
        )}
        {editable && (
          <button
            className={`icon edit ${editing ? 'on' : ''}`}
            title={editing ? 'Leave edit mode' : 'Edit: drag the point (or the contour handles) in the 3D view; points also drag in the slice views'}
            onClick={() => {
              session.setLandmarkEditing(row.key, !editing);
              if (!editing && !row.visible) session.setRowVisible(row.key, true);
            }}
          >
            <Icon name="pencil" />
          </button>
        )}
        {edited && (
          <button className="icon reset" title="Reset to the loaded version (or the AI prediction)" onClick={() => session.resetRow(row.key)}>
            <Icon name="rotate-ccw" />
          </button>
        )}
        {marginEdited && (
          <button className="icon reset" title="Reset the margin to the loaded version (or the AI prediction)" onClick={() => session.resetMargin(marginStructure)}>
            <Icon name="rotate-ccw" />
          </button>
        )}
        <button className={`icon ${row.visible ? 'on' : ''}`} title="Show / hide" onClick={() => session.setRowVisible(row.key, !row.visible)}>
          <Icon name={row.visible ? 'eye' : 'eye-off'} />
        </button>
        <button className="icon" title="Isolate" onClick={() => session.isolateRow(row.key)}>
          <Icon name="star" />
        </button>
      </div>
      {info && row.info.length > 0 && (
        <table className="inv-info">
          <tbody>
            {row.info.map(([k, v]) => (
              <tr key={k}>
                <td>{k}</td>
                <td className="mono">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
